import { useCallback } from "react";
import { useCurrentAccountId } from "@zapier/identity";
import emitUserInteractionEvent, {
  UserInteractionName,
} from "./emitUserInteractionEvent";

export type CopyToAnotherAccountEntryPoint =
  | "top_nav_kebab"
  | "row_kebab"
  | "bulk_selection"
  | "unknown";

export type CopyToAnotherAccountEventAction =
  | "open_modal"
  | "submit"
  | "success"
  | "partial_success"
  | "failure"
  // The destination account couldn't be used (e.g. its folders failed to load
  // due to a mixed-auth / SAML mismatch). Paired with `blockedReason` so we can
  // quantify how often customers hit the disabled state.
  | "blocked";

interface CopyToAnotherAccountEventInput {
  action: CopyToAnotherAccountEventAction;
  copyEntryPoint: CopyToAnotherAccountEntryPoint;
  assetsToBeCopiedCount?: number;
  assetsCopiedCount?: number;
  assetsFailedCount?: number;
  /**
   * The user's "Copy table records?" choice at copy time: `true` = copy
   * records, `false` = structure only. Only set when the choice was surfaced
   * (feature flag on + at least one table in the copy), so we can measure what
   * % of users opt in vs out.
   */
  includeTableRecords?: boolean;
  /**
   * Why a destination account was blocked (e.g. `"saml_mismatch"`). Only set
   * with the `"blocked"` action; surfaced from the BFF's classified failure
   * reason so analytics can slice the disabled-state rate by cause.
   */
  blockedReason?: string;
}

export function useEmitCopyToAnotherAccountEvents() {
  const accountId = useCurrentAccountId();

  const emit = useCallback(
    ({
      action,
      copyEntryPoint,
      assetsToBeCopiedCount,
      assetsCopiedCount,
      assetsFailedCount,
      includeTableRecords,
      blockedReason,
    }: CopyToAnotherAccountEventInput) => {
      emitUserInteractionEvent({
        accountId,
        interactionName: UserInteractionName.CopyToAnotherAccount,
        optionSelected: action,
        customTrackingFields: {
          entry_point: copyEntryPoint,
          ...(assetsToBeCopiedCount === undefined
            ? {}
            : { assets_to_be_copied_count: assetsToBeCopiedCount }),
          ...(assetsCopiedCount === undefined
            ? {}
            : { assets_copied_count: assetsCopiedCount }),
          ...(assetsFailedCount === undefined
            ? {}
            : { assets_failed_count: assetsFailedCount }),
          ...(includeTableRecords === undefined
            ? {}
            : { include_table_records: includeTableRecords }),
          ...(blockedReason === undefined
            ? {}
            : { blocked_reason: blockedReason }),
        },
      });
    },
    [accountId],
  );

  return {
    emit,
  };
}
